import type { ComponentMeta } from '../core/types';

const DYNAMIC_PREFIXES = [
  'chat.message.user.',
  'chat.message.assistant.',
  'chat.message.system.',
  'presets.item.',
  'community.card.',
  'providers.item.',
  'memory.item.',
  'gallery.item.',
  'attachments.item.',
  'admin.user.',
] as const;

const DYNAMIC_EXACT_PATTERNS = [
  /^chat\.message\.(?:user|assistant|system)\.[^.]+(?:\..+)?$/,
  /^presets\.item\.[^.]+(?:\..+)?$/,
  /^community\.card\.[^.]+(?:\..+)?$/,
  /^providers\.item\.[^.]+(?:\..+)?$/,
] as const;

const REQUIRED_PREFIXES = [
  'app.nav.',
  'chat.composer.input',
  'chat.composer.sendButton',
  'chat.composer.attachButton',
  'chat.header.backButton',
  'page.',
  'presets.root',
  'presets.saveButton',
  'page.settings.auth.',
  'page.settings.appearance.',
  'admin.',
] as const;

/** Runtime instances collapse to the reusable template that authors style. */
export function canonicalSkinId(componentId: string): string {
  for (const exact of DYNAMIC_EXACT_PATTERNS) {
    if (exact.test(componentId)) {
      return componentId.split('.').slice(0, 3).join('.');
    }
  }
  for (const prefix of DYNAMIC_PREFIXES) {
    if (componentId.startsWith(prefix)) return prefix.slice(0, -1);
  }
  return componentId;
}

export interface SkinTargetPolicy {
  selectable: boolean;
  hideable: boolean;
  editableText: boolean;
  reason: string | null;
}

export function skinTargetPolicy(meta: ComponentMeta): SkinTargetPolicy {
  const id = canonicalSkinId(meta.id);
  const required = REQUIRED_PREFIXES.some((prefix) => id === prefix || id.startsWith(prefix));
  const dynamicText = /chat\.message|\.email$|\.balance$|\.name$|\.prompt$|\.content$|^presets\.item\.|^community\.card\.|^providers\.item\.|^memory\.item\.|^gallery\.item\./.test(id);
  return {
    selectable: true,
    hideable: !required,
    editableText: (meta.role === 'text' || meta.role === 'button') && !dynamicText,
    reason: required ? '该组件承载必要导航或操作，不能隐藏' : null,
  };
}

export function markSkinTarget(element: HTMLElement | null, meta: ComponentMeta): void {
  if (element === null) return;
  const key = canonicalSkinId(meta.id);
  element.dataset.skinId = key;
  const policy = skinTargetPolicy(meta);
  element.dataset.skinHideable = policy.hideable ? 'true' : 'false';
  element.dataset.skinEditableText = policy.editableText ? 'true' : 'false';
}

